/**
 * Supabase Client Composable
 * Provides access to the Supabase client for database operations.
 *
 * Supabase客户端组合式函数
 * 提供对Supabase客户端的访问以进行数据库操作
 */

import { createClient } from '@supabase/supabase-js'

// Environment variables
// 环境变量
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

// Validate environment variables
// 验证环境变量
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. ' +
    'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env file.'
  )
}

// Storage keys / 存储键
const STORAGE_KEY = 'seebyear-auth-token'
const REMEMBER_ME_KEY = 'seebyear-remember-me'

/**
 * Custom storage adapter that switches between localStorage and sessionStorage
 * based on the "remember me" preference.
 *
 * 自定义存储适配器，根据"记住我"偏好在localStorage和sessionStorage之间切换。
 *
 * - rememberMe = true: Use localStorage (session persists after browser close)
 * - rememberMe = false: Use sessionStorage (session cleared on browser close)
 */
class SwitchableStorage {
  /**
   * Check if "remember me" is enabled
   * 检查是否启用"记住我"
   */
  private shouldRemember(): boolean {
    // Read preference from localStorage (must persist to know on page reload)
    // 从localStorage读取偏好（必须持久化以便页面重新加载时知道）
    const pref = localStorage.getItem(REMEMBER_ME_KEY)
    // Default to true if no preference set
    // 如果没有设置偏好，默认为true
    return pref !== 'false'
  }

  /**
   * Get the appropriate storage based on preference
   * 根据偏好获取适当的存储
   */
  private getStorage(): Storage {
    return this.shouldRemember() ? localStorage : sessionStorage
  }

  getItem(key: string): string | null {
    // Check both storages for existing session (migration case)
    // 检查两个存储中是否有现有会话（迁移情况）
    const fromLocal = localStorage.getItem(key)
    const fromSession = sessionStorage.getItem(key)

    // Return from the current preferred storage, or fall back to the other
    // 从当前首选存储返回，或回退到另一个
    if (this.shouldRemember()) {
      return fromLocal || fromSession
    } else {
      return fromSession || fromLocal
    }
  }

  setItem(key: string, value: string): void {
    const storage = this.getStorage()
    storage.setItem(key, value)

    // Clear from the other storage to avoid duplicates
    // 从另一个存储中清除以避免重复
    const otherStorage = storage === localStorage ? sessionStorage : localStorage
    otherStorage.removeItem(key)
  }

  removeItem(key: string): void {
    // Remove from both storages to ensure clean logout
    // 从两个存储中删除以确保干净地注销
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  }
}

/**
 * Set the "remember me" preference before login
 * 在登录前设置"记住我"偏好
 */
export function setRememberMe(remember: boolean): void {
  localStorage.setItem(REMEMBER_ME_KEY, remember ? 'true' : 'false')
}

/**
 * Get the current "remember me" preference
 * 获取当前"记住我"偏好
 */
export function getRememberMe(): boolean {
  return localStorage.getItem(REMEMBER_ME_KEY) !== 'false'
}

/**
 * Clear "remember me" preference (on logout)
 * 清除"记住我"偏好（注销时）
 */
export function clearRememberMe(): void {
  localStorage.removeItem(REMEMBER_ME_KEY)
}

// Create custom storage instance
// 创建自定义存储实例
const switchableStorage = new SwitchableStorage()

// Create Supabase client singleton
// 创建Supabase客户端单例
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Use custom storage that switches based on "remember me" preference
    // 使用根据"记住我"偏好切换的自定义存储
    storage: switchableStorage,
    storageKey: STORAGE_KEY,
  },
  db: {
    schema: 'seebyear',
  },
})

/**
 * Composable to use Supabase client
 * 使用Supabase客户端的组合式函数
 */
export function useSupabase() {
  return {
    supabase,
    supabaseUrl,
  }
}

export default useSupabase
